import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useDocumentTitle } from '../hooks/core';

export const ForgotPasswordPage: React.FC = () => {
  useDocumentTitle('Forgot Password');

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (_err) {
      // Always show success message to prevent email enumeration
      // The backend also returns success regardless of whether the email exists
      setSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Check Your Email
            </Typography>

            <Alert severity="success" sx={{ mb: 2 }}>
              If an account exists with that email address, we've sent password reset instructions.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The reset link will expire in 1 hour. If you don't see the email, check your spam folder.
            </Typography>

            <Button
              component={RouterLink}
              to="/login"
              fullWidth
              variant="outlined"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Forgot Password
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} align="center">
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} data-testid="forgot-password-form">
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              inputProps={{ 'data-testid': 'forgot-password-email-input' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              data-testid="forgot-password-submit-button"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <Typography variant="body2" align="center">
              Remember your password?{' '}
              <Link component={RouterLink} to="/login" data-testid="forgot-password-login-link">
                Back to Login
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
